import { useEffect, useState } from 'react';

import {
  AiBlock,
  Badge,
  Bars,
  Button,
  Card,
  Chart,
  Check,
  CheckGroup,
  Chip,
  ChipStrip,
  Empty,
  Field,
  Heatmap,
  PageHead,
  Progress,
  Ring,
  Rule,
  Segmented,
  Skeleton,
  Sparkline,
  Stat,
  Stepper,
  SwipeRow,
  Table,
} from '@/components/ui';
import type { Column, HeatDay, HeatWeek, Tone } from '@/components/ui';
import { cssVars, cx } from '@/lib/cx';
import { duration, km, pace } from '@/lib/format';
import { useToast } from '@/lib/toast';

import styles from './KitchenSink.module.css';

const SIGNALS = [
  { token: 'signal', name: 'Signal', sense: 'mesure, neutre' },
  { token: 'effort', name: 'Effort', sense: 'série tenue' },
  { token: 'load', name: 'Charge', sense: 'seuil approché' },
  { token: 'recover', name: 'Récup', sense: 'dette, alerte' },
] as const;

const SURFACES = [
  { token: 'bg', name: 'Fond' },
  { token: 'surface', name: 'Surface' },
  { token: 'surface-2', name: 'Surface haute' },
] as const;

/**
 * La valeur **réellement peinte** d'un token, lue sur la page.
 *
 * ── Pourquoi ce n'est pas une constante ───────────────────────────────────
 *
 * Elle en était une, et elle mentait : la liste annonçait `#C39B6E` pour une charte qui
 * portait `#E2A659`, et personne ne l'a vu — l'aplat à côté, lui, était juste. Une page
 * dont le rôle est d'être **le test visuel du projet** ne peut pas décrire une couleur
 * autrement que le reste de l'application la peint.
 *
 * C'est la règle du catalogue d'actions, appliquée à la charte : ce qui est décrit doit
 * venir de la même source que ce qui est rendu.
 *
 * ── Pourquoi elle suit le thème ───────────────────────────────────────────
 *
 * `--signal` ne vaut pas la même chose en clair et en sombre. La page montre donc la
 * palette du thème **affiché**, ce qui est la seule lecture utile ; l'observateur relit
 * quand `data-theme` change, sans quoi basculer de thème laisserait les hex du précédent.
 */
function usePaintedColours(tokens: readonly string[]): Record<string, string> {
  const [painted, setPainted] = useState<Record<string, string>>({});

  useEffect(() => {
    const read = () => {
      const style = getComputedStyle(document.documentElement);
      setPainted(
        Object.fromEntries(
          tokens.map((token) => [token, style.getPropertyValue(`--${token}`).trim().toUpperCase()]),
        ),
      );
    };
    read();

    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => {
      observer.disconnect();
    };
    // Les jetons sont des constantes de module : la liste ne change jamais d'un rendu à
    // l'autre, et la relire en dépendance relancerait l'observateur à chaque passage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return painted;
}

const SPACES = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8'] as const;

const SEANCES = ['28/07 · musculation', '25/07 · musculation', '22/07 · vélo', '19/07 · yoga'];

const PERIODS = [
  { value: '7j', label: '7J' },
  { value: '30j', label: '30J' },
  { value: '90j', label: '90J' },
  { value: 'an', label: 'AN' },
] as const;

type Period = (typeof PERIODS)[number]['value'];

// ── Données de démonstration ──────────────────────────
// Reprises de la charte. Elles ne servent qu'à cette page : aucun écran applicatif
// n'affiche de valeur inventée — dans une application de mesure ce serait la pire des
// démonstrations.

const PACE = [
  6.05, 5.98, 6.1, 5.92, 5.88, 5.95, 5.8, 5.86, 5.74, 5.79, 5.68, 5.72, 5.61, 5.66, 5.55, 5.6, 5.48,
  5.52, 5.44, 5.5, 5.38, 5.42, 5.31, 5.36, 5.26, 5.3, 5.21, 5.24, 5.16, 5.12,
];
const LOAD = [
  38, 42, 40, 47, 52, 49, 55, 58, 54, 60, 63, 59, 66, 70, 65, 72, 75, 70, 78, 74, 80, 84, 79, 86,
  90, 85, 92, 88, 94, 97,
];
const SLEEP = [
  7.6, 7.4, 7.8, 7.2, 7.5, 7.0, 7.3, 6.9, 7.1, 6.8, 7.2, 6.6, 6.9, 6.4, 6.7, 6.3, 6.6, 6.1, 6.5,
  6.0, 6.3, 5.8, 6.2, 5.6, 6.0, 5.5, 6.1, 5.4, 5.9, 5.3,
];

const LABELS = PACE.map((_, index) =>
  index === PACE.length - 1 ? 'AUJ' : `J-${PACE.length - 1 - index}`,
);

/** Grille de démonstration : une piste « deux fois par semaine », donc surtout `off`. */
function demoDays(): HeatDay[] {
  const days: HeatDay[] = [];
  const start = new Date();
  start.setDate(start.getDate() - 370);
  let seed = 7;
  const random = () => (seed = (seed * 1103515245 + 12345) % 2147483648) / 2147483648;

  for (let index = 0; index < 371; index += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const draw = random();

    if (index < 40) {
      days.push({ date: iso, value: 0, state: 'off', level: 0, reason: 'before_track' });
    } else if (index > 200 && index < 209) {
      days.push({ date: iso, value: 0, state: 'off', level: 0, reason: 'neutralised' });
    } else if (draw < 0.28) {
      days.push({
        date: iso,
        value: Math.ceil(draw * 12),
        state: 'done',
        level: Math.min(4, 1 + Math.floor(draw * 14)),
      });
    } else if (draw < 0.34) {
      days.push({ date: iso, value: 0, state: 'missed', level: 0 });
    } else if (draw > 0.97) {
      days.push({ date: iso, value: 3, state: 'bonus', level: 2 });
    } else {
      days.push({ date: iso, value: 0, state: 'off', level: 0 });
    }
  }
  return days;
}

function demoWeeks(days: readonly HeatDay[]): HeatWeek[] {
  const weeks: HeatWeek[] = [];
  for (let index = 0; index < days.length; index += 7) {
    const slice = days.slice(index, index + 7);
    const first = slice[0];
    if (!first) continue;
    const done = slice.filter((day) => day.state === 'done' || day.state === 'bonus').length;
    weeks.push({
      start: first.date,
      status: done >= 2 ? 'reached' : done === 1 ? 'partial' : 'missed',
      done,
      expected: 2,
    });
  }
  return weeks;
}

const DAYS = demoDays();
const WEEKS = demoWeeks(DAYS);

interface Session {
  date: string;
  kind: string;
  distance: number;
  minutes: number;
  label: string;
  tone: Tone;
}

const SESSIONS: Session[] = [
  {
    date: '25/07',
    kind: 'Endurance',
    distance: 8.4,
    minutes: 44.2,
    label: 'nominal',
    tone: 'effort',
  },
  {
    date: '23/07',
    kind: 'Fractionné',
    distance: 6.1,
    minutes: 32.67,
    label: 'charge',
    tone: 'load',
  },
  {
    date: '21/07',
    kind: 'Récupération',
    distance: 4.0,
    minutes: 25.97,
    label: 'relevé',
    tone: 'signal',
  },
  {
    date: '19/07',
    kind: 'Sortie longue',
    distance: 14.2,
    minutes: 78.73,
    label: 'gêne tendon',
    tone: 'recover',
  },
];

const COLUMNS: Column<Session>[] = [
  { key: 'date', header: 'Date', numeric: true, render: (row) => row.date },
  { key: 'kind', header: 'Type', render: (row) => row.kind },
  { key: 'distance', header: 'Dist.', numeric: true, render: (row) => km(row.distance) },
  { key: 'duration', header: 'Durée', numeric: true, render: (row) => duration(row.minutes) },
  {
    key: 'pace',
    header: 'Allure',
    numeric: true,
    render: (row) => pace(row.minutes / row.distance),
  },
  { key: 'state', header: 'État', render: (row) => <Badge tone={row.tone}>{row.label}</Badge> },
];

interface RoutineItem {
  label: string;
  dose?: string;
  streak?: string;
  done: boolean;
}

const ROUTINE: { group: string; items: RoutineItem[] }[] = [
  {
    group: 'Matin',
    items: [
      { label: 'Créatine', dose: '5 g', streak: '41 j', done: true },
      { label: 'Poids à jeun', dose: '68,4 kg', streak: '12 j', done: true },
      { label: 'Eau', dose: '500 ml', done: false },
    ],
  },
  {
    group: 'Séance · 21:00',
    items: [
      { label: 'Sortie course', dose: '8 km prévu', done: false },
      { label: 'Whey', dose: '30 g · après', done: false },
      { label: 'Mobilité', dose: '10 min', done: false },
    ],
  },
  {
    group: 'Soir',
    items: [
      { label: 'Magnésium', dose: '300 mg', streak: '28 j', done: true },
      { label: 'Lecture', dose: '20 min', done: false },
      { label: 'Au lit avant 23:30', done: false },
    ],
  },
];

/**
 * Référence visuelle du projet (`L03-11`).
 *
 * Chaque composant de la bibliothèque y est rendu. C'est le test visuel : une régression
 * de style saute aux yeux sur une seule page, avant d'atteindre un écran applicatif.
 */
const PAINTED = [...SIGNALS, ...SURFACES].map((entry) => entry.token);

export function KitchenSink() {
  const painted = usePaintedColours(PAINTED);
  const [period, setPeriod] = useState<Period>('30j');
  const [charge, setCharge] = useState('82,5');
  const [proposedProtein, setProposedProtein] = useState('38');
  const [reps, setReps] = useState('8');
  const [seance, setSeance] = useState(SEANCES[0]);
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      ROUTINE.flatMap((group) => group.items.map((item) => [item.label, item.done])),
    ),
  );
  const { notify } = useToast();

  const done = Object.values(checked).filter(Boolean).length;
  const total = Object.keys(checked).length;

  return (
    <div className={cx('wrap', styles.screen)}>
      <PageHead eyebrow="Référence visuelle · bibliothèque de composants" title="Charte">
        Reprise fidèle de <code>GuidelinesUI.html</code>. Cette page est le test visuel du projet :
        toute dérive de la charte se voit ici avant de se voir dans un écran.
      </PageHead>

      {/* ══ 01 COULEURS ══ */}
      <Rule>01 — Couleurs</Rule>
      <div className="grid tiles">
        {SIGNALS.map((color) => (
          <div className={styles.swatch} key={color.token}>
            <div className={styles.chip} style={{ background: `var(--${color.token})` }} />
            <div className={styles.meta}>
              <div className={styles.name}>{color.name}</div>
              <div className={styles.hex}>
                {painted[color.token] ?? '…'} · {color.sense}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid tiles mt">
        {SURFACES.map((color) => (
          <div className={styles.swatch} key={color.token}>
            <div
              className={styles.chip}
              style={{
                background: `var(--${color.token})`,
                borderBottom: color.token === 'bg' ? '1px solid var(--line)' : undefined,
              }}
            />
            <div className={styles.meta}>
              <div className={styles.name}>{color.name}</div>
              <div className={styles.hex}>{painted[color.token] ?? '…'}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ══ 02 TYPOGRAPHIE ══ */}
      <Rule>02 — Typographie</Rule>
      <div className="grid g3">
        <Card>
          <p className="eyebrow">Chiffres et titres · Barlow Condensed</p>
          <p className={cx(styles.specimen, styles.specimenCondensed)}>42,7 km</p>
          <p className={styles.note}>
            Le tableau d’affichage de RunFurther : le chiffre qu’on vient lire, les titres de page
            et de carte. Les titres de carte sont en capitales, et seulement eux.
          </p>
        </Card>
        <Card>
          <p className="eyebrow">Interface · Space Grotesk</p>
          <p className={cx(styles.specimen, styles.specimenDisplay)}>Séance du jour</p>
          <p className={styles.note}>
            Libellés, boutons, champs, corps de texte. Le corps est à 16&nbsp;px, le plancher à 12 —
            rien n’est écrit plus petit nulle part.
          </p>
        </Card>
        <Card>
          <p className="eyebrow">Data · JetBrains Mono</p>
          <p className={cx(styles.specimen, styles.specimenMono)}>04:31:08</p>
          <p className={styles.note}>
            Chasse fixe : les colonnes s'alignent seules, l'œil compare sans effort.
            <span className="num"> 0123456789 œ é à ç ù</span>
          </p>
        </Card>
      </div>

      {/* ══ 03 CONTRÔLES ══ */}
      <Rule>03 — Contrôles</Rule>
      <div className="grid g2">
        <Card>
          <h3>Boutons</h3>
          <div className="row mt">
            <Button
              variant="primary"
              onClick={() => {
                notify('Séance enregistrée.', 'effort');
              }}
            >
              Enregistrer la séance
            </Button>
            <Button variant="ghost">Voir l'historique</Button>
            <Button variant="quiet">Annuler</Button>
            <Button variant="ghost" disabled>
              Sync
            </Button>
          </div>

          <h3 className={styles.subhead}>Attente</h3>
          <p className={styles.note}>
            L'état de chargement, dessiné plutôt qu'écrit. Il occupe la place de ce qui arrive : la
            page ne saute pas quand la donnée se pose.
          </p>
          <div className="stack mt">
            <Skeleton />
          </div>
        </Card>

        <Card>
          <h3>Champs</h3>
          <div className="stack mt">
            <Field label="Distance (km)" defaultValue="8.40" />
            <Field label="Durée" defaultValue="00:44:12" />
            <Field label="Ressenti" placeholder="jambes lourdes, vent de face…" />
            <Field
              label="Poids (kg)"
              defaultValue="900"
              error="Doit être inférieur ou égal à 500"
            />
          </div>

          <h3 className={styles.subhead}>États</h3>
          <div className="row mt">
            <Badge tone="signal">relevé</Badge>
            <Badge tone="effort">série · 12 j</Badge>
            <Badge tone="load">charge +18 %</Badge>
            <Badge tone="recover">manquant</Badge>
          </div>

          <h3 className={styles.subhead}>Période</h3>
          <div className="row mt">
            <Segmented label="Période" value={period} onChange={setPeriod} options={PERIODS} />
          </div>
        </Card>
      </div>

      {/* ══ 03b TACTILE ══ */}
      <Rule>03b — Au doigt</Rule>
      <p className={cx('lede', styles.sectionLede)}>
        Cible minimale 44&nbsp;px, 56&nbsp;px pour l’action qui termine un geste. Tout ce qui suit
        s’utilise sans clavier et sans viser.
      </p>
      <div className="grid g2">
        <Card>
          <h3>Pas-à-pas</h3>
          <p className={styles.note}>
            Deux grosses touches autour de la valeur. Le champ reste saisissable : le pas-à-pas est
            un raccourci, jamais la seule porte. Sur ce qu'il ne reconnaît pas, il se désactive
            plutôt que d'écraser la saisie.
          </p>
          <div className="stack mt">
            <Stepper label="Charge (kg)" value={charge} onChange={setCharge} step={2.5} min={0} />
            <Stepper
              label="Réps"
              inputMode="numeric"
              value={reps}
              onChange={setReps}
              min={1}
              max={50}
            />
            {/* L'état « proposé » (`NUT-04`, `IMP-02`) : trait discontinu et teinte du bloc
                IA. Le champ se comporte exactement comme les deux autres — c'est le
                statut de la valeur qui change, pas ce qu'on peut en faire. */}
            <Stepper
              label="Protéines (g)"
              value={proposedProtein}
              onChange={setProposedProtein}
              step={5}
              min={0}
              proposed={proposedProtein === '38'}
              hint="proposé par l'IA — la marque disparaît dès qu'on y touche"
            />
          </div>
        </Card>

        <Card>
          <h3>Pastilles et bande glissante</h3>
          <p className={styles.note}>
            Un choix court et fréquent se montre, il ne se cache pas derrière un panneau système. La
            bande se parcourt d'un pouce et s'accroche.
          </p>
          <div className="stack mt">
            <ChipStrip label="Séance">
              {SEANCES.map((item) => (
                <Chip
                  key={item}
                  selected={item === seance}
                  onClick={() => {
                    setSeance(item);
                  }}
                >
                  {item}
                </Chip>
              ))}
            </ChipStrip>
            <ChipStrip label="Charges récentes">
              <Chip
                onClick={() => {
                  setCharge('80');
                }}
              >
                80 kg
              </Chip>
              <Chip
                onClick={() => {
                  setCharge('82,5');
                }}
              >
                82,5 kg
              </Chip>
              <Chip
                onClick={() => {
                  setCharge('85');
                }}
              >
                85 kg
              </Chip>
            </ChipStrip>
          </div>

          <h3 className={styles.subhead}>Ligne à glisser</h3>
          <p className={styles.note}>
            Tirer vers la gauche découvre l'action. Elle reste visible là où il y a un pointeur fin
            — un geste ne peut pas être la seule façon d'atteindre une commande. Deux appuis pour
            détruire : le projet n'a pas d'annulation.
          </p>
          <div className="stack mt">
            {['Course · 20/07', 'Séance · 21/07'].map((item) => (
              <SwipeRow
                key={item}
                actionLabel={`Supprimer ${item}`}
                onAction={() => {
                  notify(`${item} — supprimé.`, 'signal');
                }}
              >
                <div className={styles.swipeDemo}>{item}</div>
              </SwipeRow>
            ))}
          </div>
        </Card>
      </div>

      {/* ══ 04 CHIFFRES CLÉS ══ */}
      <Rule>04 — Chiffres clés</Rule>
      <div className="grid tiles">
        <Card>
          <Stat
            label="Volume · 7 j"
            value="31,2"
            unit="km"
            detail="▲ 12 % vs sem. préc."
            direction="up"
            spark={[8, 12, 9, 18, 16, 24, 27]}
            sparkTone="effort"
          />
        </Card>
        <Card>
          <Stat
            label="Sommeil moy."
            value="6:52"
            detail="▼ 21 min vs objectif"
            direction="down"
            spark={[24, 20, 23, 15, 17, 11, 9]}
            sparkTone="recover"
          />
        </Card>
        <Card>
          <Stat
            label="Charge aiguë"
            value="418"
            unit="ua"
            detail="ratio 1,28 — zone haute"
            spark={[10, 13, 12, 19, 21, 25, 28]}
            sparkTone="load"
          />
        </Card>
        <Card>
          <Stat
            label="Jours relevés"
            value="247"
            unit="/365"
            detail="série en cours : 12"
            spark={[14, 16, 15, 20, 19, 22, 23]}
          />
        </Card>
      </div>

      {/* ══ 05 ASSIDUITÉ ══ */}
      <Rule>05 — Assiduité</Rule>
      <Card>
        <div className="spread">
          <div>
            <h3>371 jours · piste « torse »</h3>
            <p className={styles.note}>
              Deux fois par semaine. La grille est majoritairement grise, et ce n'est{' '}
              <strong>pas</strong> un échec : gris veut dire « rien n'était attendu ce jour-là ».
              Seul le rouge accuse. Les hachures sont les jours neutralisés — une grippe ne casse
              pas une série.
            </p>
          </div>
          <Badge tone="effort">série · 12 sem.</Badge>
        </div>
        <div className="mt">
          <Heatmap
            days={DAYS}
            weeks={WEEKS}
            label="Assiduité torse"
            unit="séries"
            accentRgb="var(--effort-rgb)"
          />
        </div>
      </Card>

      {/* ══ 06 GRAPHIQUES ══ */}
      <Rule>06 — Graphiques</Rule>
      <div className="grid g2">
        <Card>
          <div className="spread">
            <div>
              <h3>Répartition du temps</h3>
              <p className={cx('eyebrow', styles.tightEyebrow)}>semaine 30</p>
            </div>
            <Badge tone="load" mono>
              62 h
            </Badge>
          </div>
          <Bars
            rows={[
              { label: 'Sommeil', ratio: 0.88, value: '48 h', tone: 'signal' },
              { label: 'Études', ratio: 0.64, value: '35 h', tone: 'effort' },
              { label: 'Projets', ratio: 0.46, value: '25 h', tone: 'load' },
              { label: 'Sport', ratio: 0.22, value: '12 h', tone: 'recover' },
            ]}
          />
          <div className={styles.blockSpaced}>
            <Ring
              ratio={0.78}
              label="Objectif hebdomadaire"
              detail="4 séances sur 5 · reste samedi"
            />
          </div>
        </Card>

        <Card>
          <h3>Régularité par item</h3>
          <p className={styles.note}>
            30 derniers jours. Ce qui décroche se voit ici avant de se voir ailleurs.
          </p>
          <Bars
            rows={[
              { label: 'Créatine', ratio: 0.97, value: '29/30', tone: 'effort' },
              { label: 'Magnésium', ratio: 0.93, value: '28/30', tone: 'effort' },
              { label: 'Whey', ratio: 0.73, value: '22/30', tone: 'signal' },
              { label: 'Mobilité', ratio: 0.47, value: '14/30', tone: 'load' },
              { label: 'Coucher', ratio: 0.3, value: '9/30', tone: 'recover' },
            ]}
          />
          <div className="mt">
            <Sparkline values={PACE.map((value) => -value)} label="Allure sur 30 jours" />
          </div>
        </Card>
      </div>

      {/* ══ 07 ANALYSE CROISÉE ══ */}
      <Rule>07 — Analyse croisée</Rule>
      <Card>
        <div className={styles.chartHead}>
          <div>
            <h3>Allure, charge et sommeil</h3>
            <p className={cx('eyebrow', styles.tightEyebrow)}>30 derniers jours</p>
          </div>
          <Segmented
            label="Période du graphique"
            value={period}
            onChange={setPeriod}
            options={PERIODS}
          />
        </div>
        <Chart
          labels={LABELS}
          primary={{
            label: 'Allure',
            unit: '/km',
            values: PACE,
            tone: 'signal',
            domain: [5.0, 6.2],
            ticks: [5.0, 5.3, 5.6, 5.9, 6.2],
            format: (value) => pace(value),
          }}
          context={{ label: 'Charge', unit: 'ua', values: LOAD, tone: 'load' }}
          band={{
            label: 'Sommeil',
            unit: 'h',
            values: SLEEP,
            tone: 'effort',
            alertBelow: 6.5,
            domain: [5, 8],
            // `unit` dit « h », `format` le seul nombre : l'infobulle colle les deux.
            format: (value) => String(Math.floor(value)),
          }}
          note={
            <>
              Les barres sous l'axe passent en <span className={styles.recoverInk}>rouge</span> sous
              6 h 30. Trois barres rouges d'affilée et la bande de charge s'épaissit : c'est le
              signal qu'on cherche à voir venir.
            </>
          }
        />
      </Card>

      {/* ══ 08 ROUTINE ══ */}
      <Rule>08 — Routine du jour</Rule>
      <div className="grid g2">
        <Card>
          <div className="spread">
            <div>
              <h3>Samedi 26 juillet</h3>
              <p className={cx('eyebrow', styles.tightEyebrow)}>jour de séance</p>
            </div>
            <Badge tone="effort">
              {done}/{total}
            </Badge>
          </div>

          {ROUTINE.map((group) => (
            <CheckGroup title={group.group} key={group.group}>
              {group.items.map((item) => (
                <Check
                  key={item.label}
                  label={item.label}
                  dose={item.dose}
                  streak={item.streak}
                  checked={checked[item.label] ?? false}
                  onToggle={() => {
                    setChecked((current) => ({
                      ...current,
                      [item.label]: !(current[item.label] ?? false),
                    }));
                  }}
                />
              ))}
            </CheckGroup>
          ))}

          <Progress done={done} total={total} />
        </Card>

        <div className="stack">
          <AiBlock
            tag="Corrélation"
            actions={<Button variant="ghost">Mettre un rappel 20:45</Button>}
          >
            <p>
              Les jours où <strong>Mobilité</strong> est cochée, ton allure du lendemain gagne{' '}
              <strong>7 s/km</strong> en moyenne. C'est l'item le moins suivi de ta routine et celui
              qui pèse le plus.
            </p>
          </AiBlock>

          <Empty
            title="Aucun relevé aujourd'hui"
            action={<Button variant="primary">Relever maintenant</Button>}
          >
            Deux chiffres suffisent pour que la journée compte.
            <br />
            Trente secondes, et la série tient.
          </Empty>
        </div>
      </div>

      {/* ══ 09 TABLEAU ══ */}
      <Rule>09 — Tableau</Rule>
      <Card flush>
        <h3 className={styles.tableTitle}>Dernières séances</h3>
        <Table
          columns={COLUMNS}
          rows={SESSIONS}
          rowKey={(row) => row.date}
          caption="Dernières séances"
        />
      </Card>

      {/* ══ 10 NIVEAUX ET ESPACEMENT ══ */}
      <Rule>10 — Niveaux d'intensité et espacement</Rule>
      <div className="grid g2">
        <Card>
          <p className={cx(styles.note, styles.noteFlush)}>
            Les quatre niveaux dérivent d'une seule couleur d'accent par opacité : chaque piste
            porte la sienne sans dupliquer quatre variantes.
          </p>
          <div className="mt">
            {SIGNALS.map((color) => (
              <div
                className={styles.levelRow}
                key={color.token}
                style={cssVars({ '--accent-rgb': `var(--${color.token}-rgb)` })}
              >
                <span className={styles.levelLabel}>{color.name}</span>
                <i className={cx(styles.cell, styles.cell0)} />
                <i className={cx(styles.cell, styles.cell1)} />
                <i className={cx(styles.cell, styles.cell2)} />
                <i className={cx(styles.cell, styles.cell3)} />
                <i className={cx(styles.cell, styles.cell4)} />
                <span className={styles.levelHint}>off · 1 · 2 · 3 · 4</span>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          {SPACES.map((space) => (
            <div className={styles.spaceRow} key={space}>
              <span>--{space}</span>
              <div className={styles.spaceBar} style={{ width: `var(--${space})` }} />
            </div>
          ))}
        </Card>
      </div>

      <Rule>Fin</Rule>
      <p className={cx('eyebrow', styles.tailNote)}>Metric — aleksi.systems</p>
    </div>
  );
}
